import { useTranslation } from "react-i18next";
import { getFusionResultLabel } from "lib/utils/fusionResult";

/*
 * TimeseriesDataTableGroup 역할:
 * - 선택된 각 파일의 대표 이미지 여부, 상세 수치, 이미지 미리보기 버튼을 표로 렌더링한다.
 * - 데스크톱용 압축 표와 레거시 상세 행을 함께 유지해 기존 레이아웃을 보존한다.
 */
export default function TimeseriesDataTableGroup({
  sortedSelectedFiles,
  fileImages,
  imageType,
  getImageSource,
  representativeUploadIdSet,
  representativeCount,
  onRepresentativeChange,
  onRemoveFile,
  currentUploadId,
  onPreviewImage,
  getDisplayText,
  formatMetric,
  formatThreePhaseDifference,
}) {
  const { i18n, t } = useTranslation();

  return (
    <>
      <div className="equipmentTimeseriesReport dataTableGroup">
        {sortedSelectedFiles.map((item) => {
          const uploadId = String(item?.upload_id ?? "");
          const isCurrent = uploadId === String(currentUploadId ?? "");
          const imageSrc = getImageSource(fileImages[uploadId], item, imageType);
          const isRepresentative = representativeUploadIdSet.has(uploadId);
          const currentFileBadgeText = t(
            "imageFileIndex.timeseries.config.currentFile",
            {
              name: "",
              defaultValue: "현재 파일",
            },
          )
            .replace(/^[\s(]+/, "")
            .replace(/\)\s*$/, "")
            .trim();
          const representativeCheckClassName = `equipmentTimeseriesReport representativeCheck${
            isRepresentative ? " isChecked" : ""
          }`;
          const summaryCellClassName = `equipmentTimeseriesReport dataTableSummaryCell${
            isCurrent ? " isCurrent" : ""
          }${isRepresentative ? " isRepresentative" : ""}`;
          const compactSummaryTableClassName = `equipmentTimeseriesReport compactSummaryTable${
            isCurrent ? " isCurrent" : ""
          }${isRepresentative ? " isRepresentative" : ""}`;
          const detailRows = [
            {
              label: t("imageFileIndex.timeseries.detail.shootingDate", {
                defaultValue: "촬영일",
              }),
              value: getDisplayText(item?.shooting_date),
            },
            {
              label: t("imageFileIndex.timeseries.detail.fileName", {
                defaultValue: "파일명",
              }),
              value: getDisplayText(item?.file_name),
            },
            {
              label: t("imageFileIndex.timeseries.detail.ultrasonic", {
                defaultValue: "초음파",
              }),
              value: formatMetric(item?.avg_decibel, " dB"),
            },
            {
              label: t("imageFileIndex.timeseries.detail.detectedTemperature", {
                defaultValue: "검출 온도",
              }),
              value: formatMetric(item?.thermal_center, " °C"),
            },
            {
              label: t("imageFileIndex.timeseries.detail.threePhaseDifference", {
                defaultValue: "3상 비교 온도차",
              }),
              value: formatThreePhaseDifference(item?.click_points),
            },
            {
              label: t("imageFileIndex.timeseries.detail.ultraviolet", {
                defaultValue: "자외선",
              }),
              value: getDisplayText(item?.uv_detected),
            },
            {
              label: t("imageFileIndex.timeseries.detail.inspectionResult", {
                defaultValue: "점검 결과",
              }),
              value: getFusionResultLabel(
                item?.fusion_result_code ?? item?.fusion_result,
                i18n,
                {
                  variant: "image",
                  fallback: getDisplayText(item?.fusion_result),
                },
              ),
            },
          ];
          const previewTitle = getDisplayText(item?.file_name);

          return (
            <table
              key={uploadId}
              className="equipmentTimeseriesReport dataTable"
            >
              <tbody>
                <tr>
                  <td
                    colSpan={3}
                    className="equipmentTimeseriesReport dataTableTop"
                  >
                    <div className="equipmentTimeseriesReport dataTableTopBar">
                      <label className={representativeCheckClassName}>
                        <input
                          type="checkbox"
                          checked={isRepresentative}
                          onChange={(event) =>
                            onRepresentativeChange(
                              uploadId,
                              event.target.checked,
                            )
                          }
                        />
                        <span>
                          {t("imageFileIndex.timeseries.detail.setRepresentative", {
                            defaultValue: "대표 이미지로 설정",
                          })}
                        </span>
                      </label>

                      {!isCurrent ? (
                        <button
                          type="button"
                          className="equipmentTimeseriesReport removeIconButton"
                          onClick={() => onRemoveFile(uploadId)}
                          aria-label={t(
                            "imageFileIndex.timeseries.config.removeFile",
                            {
                              name: getDisplayText(item?.file_name),
                              defaultValue: "{{name}} 비교에서 제외",
                            },
                          )}
                          title={t("imageFileIndex.timeseries.config.removeFile", {
                            name: getDisplayText(item?.file_name),
                            defaultValue: "{{name}} 비교에서 제외",
                          })}
                        >
                          <svg
                            className="equipmentTimeseriesReport removeIconSvg"
                            viewBox="0 0 12 12"
                            aria-hidden="true"
                          >
                            <path d="M2 2L10 10" />
                            <path d="M10 2L2 10" />
                          </svg>
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>

                <tr>
                  <td
                    colSpan={3}
                    className={summaryCellClassName}
                  >
                    {isCurrent ? (
                      <div className="equipmentTimeseriesReport summaryStateRow">
                        {isCurrent ? (
                          <span className="equipmentTimeseriesReport summaryStateBadge isCurrent">
                            {currentFileBadgeText}
                          </span>
                        ) : null}
                        {representativeCount < 0 ? (
                          <span className="equipmentTimeseriesReport summaryStateBadge isRepresentative">
                            {t("imageFileIndex.timeseries.detail.representativeImage", {
                              defaultValue: "대표 이미지",
                            })}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    <table
                      className={compactSummaryTableClassName}
                    >
                      <tbody>
                        <tr>
                          <th className="equipmentTimeseriesReport imageHeaderCell">
                            {t("imageFileIndex.timeseries.detail.image", {
                              defaultValue: "이미지",
                            })}
                          </th>
                          {detailRows.map((row) => (
                            <th
                              key={`${uploadId}-${row.label}-head`}
                              className="equipmentTimeseriesReport compactHeaderCell"
                            >
                              {row.label}
                            </th>
                          ))}
                        </tr>
                        <tr>
                          <td className="equipmentTimeseriesReport imageValueCell">
                            <PreviewTriggerButton
                              imageSrc={imageSrc}
                              previewTitle={previewTitle}
                              isRepresentative={isRepresentative}
                              onPreviewImage={onPreviewImage}
                              variant="desktop"
                            />
                          </td>
                          {detailRows.map((row) => (
                            <td
                              key={`${uploadId}-${row.label}-value-inline`}
                              className="equipmentTimeseriesReport compactValueCell"
                              title={row.value}
                            >
                              <div className="equipmentTimeseriesReport compactValueInner">
                                {row.value}
                              </div>
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>

                    <div className="equipmentTimeseriesReport dataTableSummary">
                      <PreviewTriggerButton
                        imageSrc={imageSrc}
                        previewTitle={previewTitle}
                        isRepresentative={isRepresentative}
                        onPreviewImage={onPreviewImage}
                        variant="summary"
                      />
                      <div className="equipmentTimeseriesReport summaryMeta">
                        {detailRows.map((row) => (
                          <div
                            key={`${uploadId}-${row.label}-summary`}
                            className="equipmentTimeseriesReport summaryItem"
                          >
                            <span className="equipmentTimeseriesReport summaryLabel">
                              {row.label}
                            </span>
                            <span
                              className="equipmentTimeseriesReport summaryValue"
                              title={row.value}
                            >
                              {row.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </td>
                </tr>

                <tr className="equipmentTimeseriesReport legacyDetailRow">
                  <td className="equipmentTimeseriesReport imageCell">
                    <div className="equipmentTimeseriesReport imageFrame">
                      {imageSrc ? (
                        <img
                          className="equipmentTimeseriesReport imageThumb"
                          src={imageSrc}
                          alt=""
                        />
                      ) : (
                        <div className="equipmentTimeseriesReport imageFallback">
                          {t("imageFileIndex.timeseries.detail.noImage", {
                            defaultValue: "이미지 없음",
                          })}
                        </div>
                      )}
                      {isRepresentative ? (
                        <span className="equipmentTimeseriesReport imageBadge">
                          {t("imageFileIndex.timeseries.detail.representativeImage", {
                            defaultValue: "대표 이미지",
                          })}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="equipmentTimeseriesReport labelColumn">
                    <div className="equipmentTimeseriesReport columnStack">
                      {detailRows.map((row) => (
                        <div key={`${uploadId}-${row.label}`}>{row.label}</div>
                      ))}
                    </div>
                  </td>
                  <td className="equipmentTimeseriesReport valueColumn">
                    <div className="equipmentTimeseriesReport columnStack">
                      {detailRows.map((row) => (
                        <div key={`${uploadId}-${row.label}-value`}>
                          {row.value}
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          );
        })}
      </div>

      <p className="equipmentTimeseriesReport tableNote">
        {t("imageFileIndex.timeseries.detail.note", {
          defaultValue:
            "보고서에 사용할 대표 이미지를 최대 3개 선택하세요.",
        })}
      </p>
    </>
  );
}

function PreviewTriggerButton({
  imageSrc,
  previewTitle,
  isRepresentative,
  onPreviewImage,
  variant,
}) {
  const { t } = useTranslation();
  const isSummary = variant === "summary";

  return (
    <button
      type="button"
      className="equipmentTimeseriesReport imagePreviewButton"
      onClick={() =>
        imageSrc
          ? onPreviewImage({
              src: imageSrc,
              title: previewTitle,
            })
          : null
      }
      disabled={!imageSrc}
      title={
        imageSrc
          ? t("imageFileIndex.timeseries.detail.originalImageView", {
              defaultValue: "원본 이미지 보기",
            })
          : undefined
      }
      aria-label={
        imageSrc
          ? t("imageFileIndex.timeseries.detail.originalImageView", {
              defaultValue: "원본 이미지 보기",
            })
          : undefined
      }
    >
      <div className="equipmentTimeseriesReport imageFrame">
        {imageSrc ? (
          <img
            className="equipmentTimeseriesReport imageThumb"
            src={imageSrc}
            alt=""
          />
        ) : (
          <div className="equipmentTimeseriesReport imageFallback">
            {isSummary
              ? ""
              : t("imageFileIndex.timeseries.detail.noImage", {
                  defaultValue: "이미지 없음",
                })}
          </div>
        )}
        {isRepresentative ? (
          <span className="equipmentTimeseriesReport imageBadge">
            {isSummary
              ? ""
              : t("imageFileIndex.timeseries.detail.representativeImage", {
                  defaultValue: "대표 이미지",
                })}
          </span>
        ) : null}
        {!isSummary && imageSrc ? (
          <span
            className="equipmentTimeseriesReport imageZoomIcon"
            aria-hidden="true"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 3H21V9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M21 3L14 10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9 21H3V15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3 21L10 14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        ) : null}
      </div>
      {isSummary && imageSrc ? (
        <span className="equipmentTimeseriesReport imagePreviewHint">
          {t("imageFileIndex.timeseries.detail.clickToPreview", {
            defaultValue: "원본 이미지 보기",
          })}
        </span>
      ) : null}
    </button>
  );
}
